/**
 * Pair respondents across two sets of questionnaire-response records.
 *
 * Records come from useHierarchyData (QuestionnairesResponses enriched by the
 * fetchHierarchyData Cloud Function): { Input, FormID, Persona?, ... }.
 * Pure function — no Firebase imports.
 *
 * AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
 * (pairing/counting always goes through respondent keys, never doc counts)
 * AI-CONTEXT: See docs/pitfalls.md#database-raw-input-skips-negative-mirroring
 * (scores go through _mirroredScore, never raw Input averaging)
 */

/**
 * @typedef {object} RespondentPair
 * @property {string} respondent - The shared respondent key value
 * @property {number} x - Mean numeric Input of this respondent in recordsA
 * @property {number} y - Mean numeric Input of this respondent in recordsB
 */

// Score per record: prefer the server-mirrored score (fetchHierarchyData
// annotates _mirroredScore with negative matrix rows flipped within the
// field scale); records without it (stale module cache) fall back to raw
// Input. '' / null / non-numeric must be skipped, NOT coerced to 0
// (Number('') === 0 would poison averages).
const recordScore = (record) => {
    if (record && record._mirroredScore !== undefined) return record._mirroredScore
    const value = record?.Input
    if (value === '' || value == null) return null
    const num = Number(value)
    return Number.isFinite(num) ? num : null
}

// Mean mirrored score per respondent key → Map<respondent, number>
const averageByRespondent = (records, key) => {
    /** @type {Map<string, {sum: number, count: number}>} */
    const acc = new Map()
    ;(Array.isArray(records) ? records : []).forEach((r) => {
        const respondent = r?.[key]
        const score = recordScore(r)
        // Empty-string keys (e.g. Persona: '' on anonymous responses) must
        // never pair — they would lump all anonymous respondents together
        if (!respondent || score === null) return
        const entry = acc.get(respondent) || { sum: 0, count: 0 }
        entry.sum += score
        entry.count += 1
        acc.set(respondent, entry)
    })
    const means = new Map()
    acc.forEach(({ sum, count }, respondent) => means.set(respondent, sum / count))
    return means
}

/**
 * Picks the respondent key for cross-set pairing: Persona when both sides
 * carry persona-linked records (cross-questionnaire pairing), else FormID
 * (same-form pairing).
 *
 * @param {object[]} recordsA
 * @param {object[]} recordsB
 * @returns {'Persona' | 'FormID'}
 */
export const resolvePairKey = (recordsA, recordsB) => {
    const hasPersona = (records) =>
        Array.isArray(records) && records.some((r) => r?.Persona)
    return hasPersona(recordsA) && hasPersona(recordsB) ? 'Persona' : 'FormID'
}

/**
 * @param {object[]} recordsA - Records of the first effect/question
 * @param {object[]} recordsB - Records of the second effect/question
 * @param {object} [options]
 * @param {string} [options.key] - Respondent key field (default 'FormID')
 * @returns {RespondentPair[]} One pair per respondent present on both sides
 */
const pairRespondentScores = (recordsA, recordsB, { key = 'FormID' } = {}) => {
    const meansA = averageByRespondent(recordsA, key)
    const meansB = averageByRespondent(recordsB, key)

    const pairs = []
    meansA.forEach((x, respondent) => {
        if (!meansB.has(respondent)) return
        pairs.push({ respondent, x, y: meansB.get(respondent) })
    })
    return pairs
}

export default pairRespondentScores
